import { FieldProps } from "formik";
import { Autocomplete } from "formik-mui";
import {
  AutocompleteRenderInputParams,
  ListItem,
  ListItemText,
  TextField,
} from "@mui/material";
import { useEffect } from "react";
import { ApiError, Zahllauf } from "../../api";
import { useGetZahllauf, useSucheZahllaeufe } from "../../client/hooks";
import { useQuery } from "react-query";
import { useZahllaufContext } from "../../contexts/ZahllaufContext";

interface ZahllaufSelectProps extends FieldProps {
  label: string;
  required?: boolean;
  setZahllauf: (value: string) => void;
  zahllaufBezahlt?: boolean;
}

function ZahllaufSelect({
  label,
  required,
  zahllaufBezahlt,
  field,
  form,
  meta,
  setZahllauf,
}: ZahllaufSelectProps) {
  const searchZahllaeufe = useSucheZahllaeufe();
  const ladeZahllauf = useGetZahllauf();
  const zahllaufContext = useZahllaufContext();

  useEffect(() => {
    form.setFieldValue(field.name, zahllaufContext.inputValue);
  }, [zahllaufContext.inputValue]);

  // Handle special values when field.value is a string (from URL/filter)
  useEffect(() => {
    if (field.value && typeof field.value === "string") {
      if (field.value === "_all") {
        const alleOption: Zahllauf = {
          id: "_all",
          bezahlt: false,
          bezahltAm: "",
          bezeichnung: "Alle",
          kontoId: "",
          permissions: [],
          anzahlRechnungen: 0,
          zahlbetrag: 0,
          // @ts-ignore
          created: "",
        };
        zahllaufContext.setInputValue(alleOption);
      } else if (field.value === "_nall") {
        const nichtZugewiesenOption: Zahllauf = {
          id: "_nall",
          bezahlt: false,
          bezahltAm: "",
          bezeichnung: "Nicht zugewiesen",
          kontoId: "",
          permissions: [],
          anzahlRechnungen: 0,
          zahlbetrag: 0,
          // @ts-ignore
          created: "",
        };
        zahllaufContext.setInputValue(nichtZugewiesenOption);
      }
    }
  }, [field.value]);

  // Load the current Zahllauf when field.value is a string (ID)
  const { data: currentZahllauf } = useQuery(
    ["zahllauf", field.value],
    () => ladeZahllauf(field.value as string),
    {
      enabled: !!field.value && typeof field.value === "string" &&
          field.value !== "_all" &&
          field.value !== "_nall",
      refetchOnWindowFocus: false,
    }
  );

  // Update inputValue when currentZahllauf changes (e.g., after rename)
  useEffect(() => {
    if (currentZahllauf) {
      zahllaufContext.setInputValue(currentZahllauf);
    }
  }, [currentZahllauf]);

  // Search for Zahllaeufe based on context searchValue
  const { data: zahllaeufePage } = useQuery(
    ["zahllaeufe", "search", zahllaufContext.searchValue, zahllaufBezahlt],
    async () => {
      try {
        console.log("searching for zahllauefe", zahllaufContext.searchValue);
        const zahllauefe = await searchZahllaeufe({
          zahllaufBezahlt,
          search: zahllaufContext.searchValue,
          page: 0,
          limit: 20,
          sort: "bezeichnung",
        });
        return zahllauefe;
      } catch (e) {
        throw e as ApiError;
      }
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  const optionen: Zahllauf[] = [
    {
      id: "_all",
      bezahlt: false,
      bezahltAm: "",
      bezeichnung: "Alle",
      kontoId: "",
      permissions: [],
      anzahlRechnungen: 0,
      zahlbetrag: 0,
      // @ts-ignore
      created: "",
    },
    {
      id: "_nall",
      bezahlt: false,
      bezahltAm: "",
      bezeichnung: "Nicht zugewiesen",
      kontoId: "",
      permissions: [],
      anzahlRechnungen: 0,
      zahlbetrag: 0,
      // @ts-ignore
      created: "",
    },
    ...(zahllaeufePage?.items ?? []),
  ];

  return (
    <Autocomplete
      sx={{ width: "250px", ml: 2 }}
      size="small"
      field={field}
      form={form}
      meta={meta}
      // @ts-ignore
      value={zahllaufContext.inputValue}
      filterSelectedOptions
      filterOptions={(x) => x}
      options={optionen}
      getOptionLabel={(option: string | Zahllauf) => {
        if (typeof option === "string") {
          return option;
        } else {
          return option.bezeichnung;
        }
      }}
      onChange={(event, value) => {
        zahllaufContext.setInputValue(value);
        if (value as Zahllauf) {
          const zahllaufId =
            typeof value === "string" ? value : (value as Zahllauf)?.id ?? "";
          setZahllauf(zahllaufId);
          // Update context with selected zahllauf ID
          zahllaufContext.setSelectedZahllaufId(zahllaufId);
        }
      }}
      onInputChange={(event, value) => {
        zahllaufContext.setSearchValue(value);
      }}
      renderOption={(props, option: Zahllauf) => {
        return (
          <ListItem {...props} key={option.id}>
            <ListItemText primary={option.bezeichnung} />
          </ListItem>
        );
      }}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField {...params} label={label + (required ? " *" : "")} />
      )}
    />
  );
}

export default ZahllaufSelect;
